import { motion } from "motion/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import EmailPassword from "supertokens-web-js/recipe/emailpassword";
import { createCode } from "supertokens-web-js/recipe/passwordless";

import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { IconButton } from "@/components/Button/IconButton";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { EyeIcon } from "@/components/Icons/EyeIcon";
import { EyeOffIcon } from "@/components/Icons/EyeOffIcon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { removeFromHeaders } from "@/helpers/headers";
import { isHttpError } from "@/helpers/Network/errors";
import { createEmailValidRule, createRequiredStringRule } from "@/helpers/rules";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useBool } from "@/hooks/useBool";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useConfig } from "@/providers/config/useConfig";
import { usePublicAppBranding } from "@/providers/PublicAppBrandingProvider";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { PasswordHelper } from "../components/PasswordHelper";

interface FormValues {
  email: string;
  password: string;
}

const MAGIC_LINK_FOR_SIGN_UP = false;

enum PageState {
  Initial,
  LogIn,
  SignUp,
}

export function SignInSignUpPage(): React.ReactNode {
  const showFlashToast = useFlashToast();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { appBranding } = usePublicAppBranding();
  const authServerUrl = useConfig("authenticationServerRootUrl");

  const [globalError, setGlobalError] = useState<string>();
  const [showPassword, showPasswordHandlers] = useBool(false);
  const [state, setState] = useState(PageState.Initial);
  const [isSubmitting, submittingHandlers] = useBool(false);
  const [submitCountInInitialScreen, setSubmitCountInInitialScreen] = useState(0);

  const lastUsedEmail = useMemo(
    () => getLocalStorageValue<string | undefined>(LOCAL_STORAGE_KEYS.LAST_USED_EMAIL, undefined),
    [],
  );
  const formMethods = useForm<FormValues>({
    defaultValues: {
      email: location.state?.email || lastUsedEmail || "",
      password: "",
    },
  });
  const { setError } = formMethods;
  const submitCount = formMethods.formState.submitCount;

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  async function onSubmit(data: FormValues) {
    try {
      submittingHandlers.setTrue();
      if (state === PageState.Initial) {
        await onCheckEmail(data.email.trim());
      } else if (state === PageState.LogIn) {
        await onLogin(data.email.trim(), data.password);
      } else if (state === PageState.SignUp) {
        await onSignUp(data.email.trim(), data.password);
      } else {
        throw new Error("Invalid state");
      }
    } finally {
      submittingHandlers.setFalse();
    }
  }

  const onCheckEmail = useCallback(
    async (email: string) => {
      updateLocalStorage(LOCAL_STORAGE_KEYS.LAST_USED_EMAIL, email);

      try {
        const url = new URL(`${authServerUrl}/auth/account/exists`);
        url.searchParams.set("email", email);

        const res = await fetch(url.toString());
        const data = (await res.json()) as { status: string };

        if (data.status === "ACCOUNT_EXISTS" || (MAGIC_LINK_FOR_SIGN_UP && data.status === "ACCOUNT_NOT_FOUND")) {
          const codeResponse = await createCode({
            email,
            options: {
              preAPIHook: (x) => {
                const codeUrl = new URL(x.url);
                codeUrl.searchParams.set("languageId", i18n.language);
                if (appBranding !== DEFAULT_APP_BRANDING) {
                  codeUrl.searchParams.set("brand", appBranding);
                }

                return Promise.resolve({
                  ...x,
                  url: codeUrl.toString(),
                  requestInit: removeFromHeaders(x.requestInit, "Authorization"),
                });
              },
            },
          });

          if (codeResponse.status === "SIGN_IN_UP_NOT_ALLOWED") {
            setGlobalError(codeResponse.reason);
          } else {
            const params = new URLSearchParams({ email });
            void navigate(routes.authentication.magicLinkSent() + "?" + params.toString());
          }
        } else {
          setState(data.status === "ACCOUNT_EXISTS_WITH_PASSWORD" ? PageState.LogIn : PageState.SignUp);
          setSubmitCountInInitialScreen(submitCount + 1);
          setTimeout(() => {
            passwordRef.current?.focus();
          }, 10);
        }
      } catch (err: any) {
        if (err.isSuperTokensGeneralError === true) {
          setGlobalError(err.message);
        } else if (isHttpError(err)) {
          if (err.status === 429) {
            setGlobalError(t("page.login.errors.too-many-attempts"));
          } else {
            setGlobalError(t("page.login.errors.unknown"));
          }
        } else {
          showFlashToast({ variant: "alert", message: t("common.generic-error") });
        }
      }
    },
    [authServerUrl, showFlashToast, submitCount, t, i18n.language, appBranding, navigate],
  );

  const onLogin = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await EmailPassword.signIn({
          formFields: [
            {
              id: "email",
              value: email,
            },
            {
              id: "password",
              value: password,
            },
          ],
          options: {
            preAPIHook: (x) => {
              const url = new URL(x.url);
              url.searchParams.set("languageId", i18n.language);
              if (appBranding !== DEFAULT_APP_BRANDING) {
                url.searchParams.set("brand", appBranding);
              }

              return Promise.resolve({
                ...x,
                url: url.toString(),
                requestInit: removeFromHeaders(x.requestInit, "Authorization"),
              });
            },
          },
        });

        if (response.status === "FIELD_ERROR") {
          response.formFields.forEach((formField) => {
            if (formField.id === "email") {
              setError("email", { type: "manual", message: formField.error });
            }
          });
        } else if (response.status === "WRONG_CREDENTIALS_ERROR") {
          setGlobalError(t("page.login.errors.wrong-credentials"));
        } else if (response.status === "SIGN_IN_NOT_ALLOWED") {
          setGlobalError(response.reason);
        } else {
          let redirectPath = new URLSearchParams(window.location.search).get("redirect") || "/";
          if (!redirectPath.startsWith("/")) {
            redirectPath = "/" + redirectPath;
          }

          const redirectUrl = new URL(redirectPath, window.location.origin);

          if (appBranding !== DEFAULT_APP_BRANDING) {
            redirectUrl.searchParams.set("brand", appBranding);
          }

          void navigate(redirectUrl.pathname + redirectUrl.search);
        }
      } catch (err: any) {
        if (err.isSuperTokensGeneralError === true) {
          setGlobalError(err.message);
        } else if (isHttpError(err)) {
          setGlobalError(t("page.login.errors.unknown"));
        } else {
          showFlashToast({ variant: "alert", message: t("common.generic-error") });
        }
      }
    },
    [navigate, setError, showFlashToast, t, i18n.language, appBranding],
  );

  const onSignUp = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await EmailPassword.signUp({
          formFields: [
            {
              id: "email",
              value: email,
            },
            {
              id: "password",
              value: password,
            },
          ],
          options: {
            preAPIHook: (x) => {
              const url = new URL(x.url);
              url.searchParams.set("languageId", i18n.language);
              if (appBranding !== DEFAULT_APP_BRANDING) {
                url.searchParams.set("brand", appBranding);
              }

              return Promise.resolve({
                ...x,
                url: url.toString(),
                requestInit: removeFromHeaders(x.requestInit, "Authorization"),
              });
            },
          },
        });

        if (response.status === "FIELD_ERROR") {
          for (const formField of response.formFields) {
            if (formField.id === "email") {
              setError("email", { type: "manual", message: formField.error });
            } else if (formField.id === "password") {
              setError("password", { type: "manual", message: formField.error });
            }
          }
        } else if (response.status === "SIGN_UP_NOT_ALLOWED") {
          setError("email", { type: "manual", message: response.reason });
        } else {
          const otherQuery = location.search.replace("?", "");
          const query = `?from=signup${otherQuery ? "&" + otherQuery : ""}`;
          void navigate(routes.authentication.verifyEmail() + query, { state: { noLoader: true } });
        }
      } catch (err: any) {
        if (err.isSuperTokensGeneralError === true) {
          setError("email", { type: "manual", message: err.message });
        } else {
          setError("email", {
            type: "manual",
            message: t("page.login.errors.unknown"),
          });
        }
      }
    },
    [navigate, setError, t, location.search, appBranding, i18n.language],
  );

  const password = useWatch({ control: formMethods.control, name: "password" });

  const hasSubmittedPassword = submitCount > submitCountInInitialScreen;

  let languagePath = "/";
  if (i18n.language === "de") {
    languagePath = "/de/";
  } else if (i18n.language === "en") {
    languagePath = "/en/";
  }

  let header = t("page.login.title");
  if (state === PageState.LogIn) {
    header = t("page.login.welcome-back.title");
  } else if (state === PageState.SignUp) {
    header = t("page.login.sign-up.title");
  }

  return (
    <AuthenticationPage
      header={header}
      body={t("page.login.subtitle")}
      step={state === PageState.SignUp ? 1 : undefined}
      totalSteps={state === PageState.SignUp ? 2 : undefined}
    >
      <Form.Root onSubmit={onSubmit} formMethods={formMethods}>
        <Stack gap="lg">
          <Stack gap="md">
            {globalError && (
              <aside
                className="relative flex items-center rounded-sm bg-background-danger-secondary p-space-sm text-center text-caption text-text-danger-secondary"
                role="alert"
              >
                <span className="flex-1 px-space-xl">{globalError}</span>
                <span className="absolute right-0">
                  <IconButton
                    title={t("common.action.close")}
                    variant="ghostSecondary"
                    onClick={() => setGlobalError(undefined)}
                    icon={<XCloseIcon />}
                  />
                </span>
              </aside>
            )}
            <section className="text-left">
              <FormField label={t("page.login.form.email.label")}>
                <FormInput<FormValues>
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.login.form.email.label"),
                      isValid: createEmailValidRule(t),
                    },
                  }}
                  placeholder={t("page.login.form.email.placeholder")}
                  inputRef={emailRef}
                  id="email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
                  autoFocus
                  aria-required
                  disabled={state !== PageState.Initial || isSubmitting}
                  postfix={
                    state !== PageState.Initial && (
                      <IconButton
                        title={t("page.login.form.email.change")}
                        onClick={() => {
                          setState(PageState.Initial);
                          setGlobalError(undefined);
                          // Time out to make sure the input is no longer readonly after rerender
                          setTimeout(() => {
                            if (emailRef.current) {
                              emailRef.current.focus();

                              // Move cursor to end of input
                              const inputType = emailRef.current.type;
                              emailRef.current.type = "text";
                              emailRef.current.selectionStart = emailRef.current.selectionEnd =
                                emailRef.current.value.length;
                              emailRef.current.type = inputType;
                            }
                          }, 10);
                        }}
                        variant="ghostSecondary"
                        icon={<Edit05Icon />}
                      />
                    )
                  }
                />
              </FormField>
              <motion.div
                aria-hidden={state === PageState.Initial}
                inert={state === PageState.Initial}
                className={state === PageState.Initial ? "pointer-events-none" : "mt-space-md"}
                variants={{
                  open: { opacity: 1, height: "auto" },
                  closed: { opacity: 0, height: 0 },
                }}
                initial="closed"
                animate={state === PageState.Initial ? "closed" : "open"}
              >
                <FormField label={t("page.login.form.password.label")}>
                  <FormInput<FormValues>
                    inputRef={passwordRef}
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    hideErrorText={state === PageState.SignUp}
                    autoComplete="current-password"
                    autoCapitalize="none"
                    spellCheck="false"
                    aria-required
                    rules={{
                      validate: {
                        ...(state === PageState.LogIn
                          ? { required: createRequiredStringRule(t, "page.login.form.password.label") }
                          : {}),
                        ...(state === PageState.SignUp
                          ? {
                              required: createRequiredStringRule(t, "page.login.form.password.label"),
                              minCharacters(x) {
                                if (x.length < 8) {
                                  return t("page.login.errors.password-minimum-chars", { chars: 8 });
                                }
                              },
                              containsLowercase(x) {
                                if (x.toUpperCase() === x) {
                                  return t("page.login.errors.password-lowercase");
                                }
                              },
                              containsNumber(x) {
                                if (!/\d/.test(x)) {
                                  return t("page.login.errors.password-number");
                                }
                              },
                            }
                          : {}),
                      },
                    }}
                    postfix={
                      <IconButton
                        title={
                          showPassword
                            ? t("page.login.form.password.toggle-hidden")
                            : t("page.login.form.password.toggle-visible")
                        }
                        onClick={showPasswordHandlers.toggle}
                        variant="ghostSecondary"
                        icon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      />
                    }
                  />

                  {state === PageState.SignUp && <PasswordHelper {...{ password, hasSubmittedPassword }} />}
                </FormField>
              </motion.div>
            </section>
            <footer className="mt-space-sm flex flex-col items-center gap-space-md">
              <Button isLoading={isSubmitting} type="submit" fullWidth>
                {state === PageState.Initial && t("page.login.form.submit")}
                {state === PageState.LogIn && t("page.login.form.login.submit")}
                {state === PageState.SignUp && t("page.login.form.signup.submit")}
              </Button>
              {state === PageState.LogIn && (
                <Button
                  data-testid="forgot-password-anchor"
                  variant="ghostPrimary"
                  type="link"
                  href={
                    routes.authentication.forgotPassword() +
                    (appBranding !== DEFAULT_APP_BRANDING
                      ? `?${new URLSearchParams({ brand: appBranding }).toString()}`
                      : "")
                  }
                  state={{
                    email: formMethods.getValues("email"),
                  }}
                >
                  {t("page.login.form.forgot-password")}
                </Button>
              )}
            </footer>
          </Stack>
          {state === PageState.Initial && (
            <p className="text-overline **:text-overline">
              <Trans
                i18nKey="page.login.form.license"
                components={{
                  newline: <br />,
                  terms: (content) => (
                    <Button
                      variant="ghostPrimary"
                      type="link"
                      isExternal
                      href={`https://areaofpeople.com${languagePath}license-agreement`}
                    >
                      {content}
                    </Button>
                  ),
                  policy: (content) => (
                    <Button
                      variant="ghostPrimary"
                      type="link"
                      isExternal
                      href={`https://areaofpeople.com${languagePath}privacy-policy`}
                    >
                      {content}
                    </Button>
                  ),
                }}
              />
            </p>
          )}
        </Stack>
      </Form.Root>
    </AuthenticationPage>
  );
}
